import { useEffect, useMemo, useState } from 'react';
import { useAppState } from '../state/AppState';
import { evaluateRecord } from '../domain/completeness';
import { getAuditForClient } from '../persistence/db';
import { RiskBadge } from './RiskBadge';
import { FindingBadge } from './FindingBadge';
import type { AuditEntry } from '../domain/types';
import styles from './ClientDetail.module.css';

export function ClientDetail({ clientId }: { clientId: string }) {
  const { state, setView } = useAppState();
  const record = state.clients.find((c) => c.client_id === clientId);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [auditError, setAuditError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getAuditForClient(clientId)
      .then((entries) => {
        if (!cancelled) setAudit(entries);
      })
      .catch((err) => {
        if (!cancelled) {
          setAuditError(err instanceof Error ? err.message : 'Failed to load audit trail');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const findings = useMemo(() => {
    if (!record || !state.rules) return null;
    return evaluateRecord(record, state.rules);
  }, [record, state.rules]);

  if (!record) {
    return (
      <section className={styles.wrap}>
        <button type="button" className={styles.back} onClick={() => setView({ kind: 'list' })}>
          ← Back to audit list
        </button>
        <div className={styles.notFound}>Record {clientId} not found.</div>
      </section>
    );
  }

  return (
    <section className={styles.wrap}>
      <button type="button" className={styles.back} onClick={() => setView({ kind: 'list' })}>
        ← Back to audit list
      </button>

      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>{record.client_name}</h1>
          <div className={styles.meta}>
            <span>{record.client_id}</span>
            <span>·</span>
            <span>{record.branch}</span>
            <span>·</span>
            <span>Onboarded {record.onboarding_date}</span>
          </div>
        </div>
        <div className={styles.headerBadges}>
          {findings && <RiskBadge tier={findings.computed.tier} size="lg" />}
        </div>
      </header>

      {findings && (findings.discrepancy || findings.incomplete.length > 0) && (
        <div className={styles.findings} role="region" aria-label="Compliance findings">
          <h2 className={styles.findingsTitle}>Compliance findings</h2>
          <div className={styles.findingsBody}>
            {findings.discrepancy && (
              <div className={styles.findingItem}>
                <FindingBadge kind="discrepancy">Discrepancy</FindingBadge>
                <div className={styles.findingText}>
                  Stored classification is <strong>{findings.storedTier || 'none'}</strong>{' '}
                  but the rules engine computes <strong>{findings.computed.tier}</strong>. This
                  is a regulatory finding under MLR 2017.
                </div>
              </div>
            )}
            {findings.incomplete.map((iss) => (
              <div key={iss.field} className={styles.findingItem}>
                <FindingBadge kind="incomplete">Incomplete</FindingBadge>
                <div className={styles.findingText}>{iss.message}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={styles.grid}>
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Recorded data</h2>
          <dl className={styles.dl}>
            <Pair label="Client type" value={record.client_type} />
            <Pair label="Country of tax residence" value={record.country_of_tax_residence} />
            <Pair
              label="Annual income"
              value={`£${record.annual_income.toLocaleString('en-GB')}`}
            />
            <Pair label="Source of funds" value={record.source_of_funds} />
            <Pair label="PEP" value={record.pep_status ? 'TRUE' : 'FALSE'} />
            <Pair
              label="Sanctions match"
              value={record.sanctions_screening_match ? 'TRUE' : 'FALSE'}
            />
            <Pair label="Adverse media" value={record.adverse_media_flag ? 'TRUE' : 'FALSE'} />
            <Pair
              label="ID verification date"
              value={record.id_verification_date ?? '— (missing)'}
            />
            <Pair
              label="Documentation complete"
              value={record.documentation_complete ? 'TRUE' : 'FALSE'}
            />
            <Pair label="KYC status" value={record.kyc_status || '— (missing)'} />
            <Pair
              label="Relationship manager"
              value={record.relationship_manager || '— (missing)'}
            />
          </dl>
        </div>

        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Risk classification</h2>
          {findings && (
            <>
              <div className={styles.tierRow}>
                <div className={styles.tierLabel}>Computed by rules engine</div>
                <RiskBadge tier={findings.computed.tier} />
              </div>
              <div className={styles.tierRow}>
                <div className={styles.tierLabel}>Recorded on file</div>
                {findings.storedTier ? (
                  <RiskBadge tier={findings.storedTier} />
                ) : (
                  <span className={styles.muted}>— (missing)</span>
                )}
              </div>
              <div className={styles.reasonsTitle}>Triggers</div>
              <ul className={styles.reasons}>
                {findings.computed.reasons.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
              <div className={styles.rulesVersion}>
                Rules version: {findings.computed.rulesVersion}
              </div>
            </>
          )}
        </div>
      </div>

      <div className={styles.card}>
        <h2 className={styles.cardTitle}>Audit trail</h2>
        {auditError && (
          <div className={styles.findingText} role="alert">
            {auditError}
          </div>
        )}
        {audit.length === 0 && !auditError && (
          <div className={styles.muted}>No audit entries for this client.</div>
        )}
        {audit.length > 0 && (
          <ol className={styles.audit}>
            {audit.map((e) => (
              <li key={e.id} className={styles.auditItem}>
                <div className={styles.auditMeta}>
                  <span className={styles.auditAction}>{e.action.replace('_', ' ')}</span>
                  <span className={styles.muted}>{new Date(e.timestamp).toLocaleString()}</span>
                </div>
                <div className={styles.auditBody}>
                  <div>
                    <strong>Actor:</strong> {e.actor}
                  </div>
                  <div>
                    <strong>Computed tier:</strong>{' '}
                    <RiskBadge tier={e.computedTier} size="sm" />
                  </div>
                  <div>
                    <strong>Rules version:</strong> {e.rulesVersion}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </section>
  );
}

function Pair({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className={styles.dt}>{label}</dt>
      <dd className={styles.dd}>{value}</dd>
    </>
  );
}
